import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  LogIn, User, Briefcase, Building2, Shield, Users, Eye, EyeOff,
  CheckCircle, Loader2, ArrowLeft, GraduationCap, Mail, Phone,
  Lock, ChevronRight, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Role configurations
const ROLES = [
  { 
    id: 'student', 
    label: 'Student / Learner', 
    icon: GraduationCap, 
    color: 'blue',
    description: 'Access courses, assessments & certificates',
    gradient: 'from-blue-500 to-cyan-500'
  },
  { 
    id: 'center', 
    label: 'Training Center', 
    icon: Building2, 
    color: 'purple',
    description: 'Manage students, fees & operations',
    gradient: 'from-purple-500 to-pink-500'
  },
  { 
    id: 'employer', 
    label: 'Employer / Company', 
    icon: Briefcase, 
    color: 'emerald',
    description: 'Post jobs & hire skilled candidates',
    gradient: 'from-emerald-500 to-teal-500'
  },
  { 
    id: 'admin', 
    label: 'Admin', 
    icon: Shield, 
    color: 'red',
    description: 'System administration',
    gradient: 'from-red-500 to-orange-500'
  }
];

const UnifiedLoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useContext(AuthContext);
  
  // Get role from URL params or default to student
  const initialRole = searchParams.get('role') || 'student';
  
  const [selectedRole, setSelectedRole] = useState(initialRole);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    identifier: '', // email or mobile
    password: ''
  });

  // Get role config
  const roleConfig = ROLES.find(r => r.id === selectedRole) || ROLES[0];
  const RoleIcon = roleConfig.icon;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.identifier || !formData.password) {
      toast.error('Please fill all fields');
      return;
    }
    
    setIsLoading(true);
    
    try {
      let response;
      let redirectPath = '/';
      
      // Determine email format
      const email = formData.identifier.includes('@') 
        ? formData.identifier 
        : formData.identifier;
      
      if (selectedRole === 'center') {
        // Training Center Login
        response = await axios.post(`${API}/center/login`, {
          email: email,
          password: formData.password
        });
        const centerData = response.data.center || response.data.center_user || {};
        localStorage.setItem('center_token', response.data.token);
        localStorage.setItem('center_info', JSON.stringify(centerData));
        redirectPath = '/center/dashboard';
        const centerName = centerData.owner_name || centerData.spoc_name || centerData.organization_name || 'Partner';
        toast.success(`Welcome ${centerName}!`);
        
      } else if (selectedRole === 'admin') {
        // Try Sub-Admin first, then Super Admin
        try {
          response = await axios.post(`${API}/sub-admin/login`, {
            email: email,
            password: formData.password
          });
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('sub_admin_info', JSON.stringify(response.data.user));
          // Clear any existing super admin data
          localStorage.removeItem('admin_token');
          login(response.data.user, response.data.token);
          toast.success(`Welcome ${response.data.user.name}! (${response.data.user.role_name})`);
          redirectPath = '/admin/dashboard';
        } catch {
          // Try super admin
          response = await axios.post(`${API}/auth/login`, {
            email: email,
            password: formData.password
          });
          if (response.data.user.role !== 'admin') {
            throw new Error('Not an admin account');
          }
          // Clear any existing sub-admin data for clean super admin session
          localStorage.removeItem('sub_admin_info');
          login(response.data.user, response.data.token);
          toast.success('Welcome Super Admin!');
          redirectPath = '/admin/dashboard';
        }
        
      } else {
        // Student/Employer Login
        response = await axios.post(`${API}/auth/login`, {
          email: email,
          password: formData.password
        });
        
        if (response.data.user.role !== selectedRole) {
          toast.error(`This account is registered as ${response.data.user.role}, not ${selectedRole}`);
          setIsLoading(false);
          return;
        }
        
        login(response.data.user, response.data.token);
        toast.success(`Welcome ${response.data.user.name}!`);
        
        redirectPath = selectedRole === 'student' 
          ? '/student/dashboard' 
          : '/employer/dashboard';
      }
      
      // Remember me
      if (rememberMe) {
        localStorage.setItem('remembered_email', formData.identifier);
      }
      
      navigate(redirectPath);
      
    } catch (error) {
      let errorMsg = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      // Handle validation errors (array format from Pydantic)
      if (Array.isArray(errorMsg)) {
        errorMsg = errorMsg[0]?.msg || 'Login failed';
      } else if (typeof errorMsg === 'object') {
        errorMsg = errorMsg.msg || errorMsg.message || 'Login failed';
      }
      toast.error(String(errorMsg));
    } finally {
      setIsLoading(false);
    }
  };

  // Load remembered email
  useEffect(() => {
    const remembered = localStorage.getItem('remembered_email');
    if (remembered) {
      setFormData(prev => ({ ...prev, identifier: remembered }));
      setRememberMe(true);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 py-8">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/10 via-transparent to-emerald-500/10"></div>
      </div>
      
      <div className="relative max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">
              <span className="text-blue-400">Iqra</span>
              <span className="text-emerald-400"> Skills</span>
            </h1>
          </Link>
          <p className="text-slate-400 mt-2">Sign in to your account</p>
        </div>

        {/* Main Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl overflow-hidden">
          {/* Role Selector */}
          <div className="p-6 border-b border-white/10">
            <Label className="text-white/80 text-sm mb-3 block">I am a</Label>
            <div className="grid grid-cols-2 gap-2">
              {ROLES.filter(r => r.id !== 'admin').map(role => {
                const Icon = role.icon;
                const isSelected = selectedRole === role.id;
                return (
                  <button
                    key={role.id}
                    onClick={() => setSelectedRole(role.id)}
                    className={`p-3 rounded-xl border transition-all text-left ${
                      isSelected 
                        ? `bg-gradient-to-r ${role.gradient} border-transparent text-white` 
                        : 'border-white/20 hover:border-white/40 text-white/70 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-5 h-5" />
                      <span className="text-sm font-medium">{role.label.split('/')[0]}</span>
                    </div>
                  </button>
                );
              })}
            </div>
            {/* Admin link */}
            <button
              onClick={() => setSelectedRole('admin')}
              className={`mt-2 w-full p-2 rounded-lg text-xs flex items-center justify-center gap-1 transition-all ${
                selectedRole === 'admin' 
                  ? 'bg-red-500/20 text-red-300 border border-red-500/50' 
                  : 'text-white/40 hover:text-white/60'
              }`}
            >
              <Shield className="w-3 h-3" /> Admin Login
            </button>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Role Info */}
            <div className={`p-3 rounded-xl bg-gradient-to-r ${roleConfig.gradient} bg-opacity-20`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                  <RoleIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-medium">{roleConfig.label}</p>
                  <p className="text-white/70 text-xs">{roleConfig.description}</p>
                </div>
              </div>
            </div>

            {/* Identifier Field */}
            <div>
              <Label className="text-white/80">Email or Mobile</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  type="text"
                  value={formData.identifier}
                  onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                  placeholder="Enter email or mobile number"
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12"
                  data-testid="login-identifier"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <Label className="text-white/80">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password"
                  className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-white/30 bg-white/10 text-blue-500"
                />
                Remember me
              </label>
              <Link 
                to="/auth/forgot-password" 
                className="text-sm text-blue-400 hover:text-blue-300"
              >
                Forgot Password?
              </Link>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading}
              className={`w-full h-12 rounded-xl text-white font-semibold bg-gradient-to-r ${roleConfig.gradient} hover:opacity-90 transition-all`}
              data-testid="login-submit"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" /> Sign In
                </>
              )}
            </Button>
          </form>

          {/* Register Link - Role Specific */}
          <div className="p-6 pt-0 text-center">
            {selectedRole === 'student' && (
              <p className="text-white/50 text-sm">
                Don't have an account?{' '}
                <Link 
                  to="/auth/register" 
                  className="text-blue-400 hover:text-blue-300 font-medium"
                >
                  Enroll as Student <ChevronRight className="w-4 h-4 inline" />
                </Link>
              </p>
            )}
            {selectedRole === 'employer' && (
              <p className="text-white/50 text-sm">
                Don't have an account?{' '}
                <Link 
                  to="/auth/register" 
                  className="text-emerald-400 hover:text-emerald-300 font-medium"
                >
                  Register Company <ChevronRight className="w-4 h-4 inline" />
                </Link>
              </p>
            )}
            {selectedRole === 'center' && (
              <p className="text-white/50 text-sm">
                Want to become a partner?{' '}
                <Link 
                  to="/partner-apply" 
                  className="text-purple-400 hover:text-purple-300 font-medium"
                >
                  Apply as Partner <ChevronRight className="w-4 h-4 inline" />
                </Link>
              </p>
            )}
            {selectedRole === 'admin' && (
              <p className="text-white/50 text-sm">
                Admin accounts are created by Super Admin
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-white/30 text-xs mt-6">
          © 2026 Iqra Skills Pvt Ltd. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default UnifiedLoginPage;
